/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { AliasType, ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './BreadcrumbNavigation';
import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { helpRenders } from './BreadcrumbNavigation_Help';

const BreadcrumbNavigationManager: ComponentManagerFn<
  'BreadcrumbNavigation'
> = (platform: Platform) => {
  return {
    ...baseComponentManager<'BreadcrumbNavigation'>(
      platform,
      'BreadcrumbNavigation',
    ),
    type: 'BreadcrumbNavigation',
    name: 'Breadcrumb Navigation',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'TriggerOnClick',
          'TriggerOnCreate',
          'TriggerOnInitialize',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      comboDatasourceAlias: {
        label: 'Data Store Alias',
        type: 'DataSourceAliasChooser',
        category: 'Select Options',
        allowedTypes: [
          'CloudDataStore',
          'ReadOnlyDataStore',
          'LocalDataStore',
          'DataStore',
        ],
        required: true,
      },
      labelAttribute: {
        aliasType: 'LOV',
        getAttributeDataTypes: () => ['String'],
        label: 'Label Attribute',
        category: 'Select Options',
        required: true,
        type: 'ComboAttributeChooser',
      },
      idAttribute: {
        label: 'ID Attribute',
        type: 'ComboAttributeChooser',
        aliasType: 'LOV',
        category: 'Select Options',
        required: true,
        getAttributeDataTypes: () => ['String', 'Integer', 'UserID', 'Decimal'],
      },
      parentIdAttribute: {
        label: 'Parent ID Attribute',
        type: 'ComboAttributeChooser',
        aliasType: 'LOV',
        category: 'Select Options',
        required: true,
        getAttributeDataTypes: () => ['String', 'Integer', 'UserID', 'Decimal'],
      },
      doNotPrefetch: {
        label: 'Do Not Prefetch',
        type: 'boolean',
        category: 'Select Options',
      },
      initialSelection: {
        label: 'Initial Selection',
        type: 'select',
        options: toOptions(['deep', 'first', 'none', 'deepButLast']),
        category: 'Select Options',
        helpText: `Choose \`Deep\` to select the first root option and all the way down to it's first leaf option. Choose \`First\` to only select the first root option without selecting any of the child options.
          \`Deep But Last\` is similar to \`Deep\` except that it will leave the last level unselected.`,
      },
      maxVisibleLevels: {
        label: 'Max Visible Level Expression',
        type: 'expressionOnly',
        returnType: () => 'number',
        category: 'Select Options',
      },
      placeholder: {
        label: 'Placeholder Expression',
        type: 'expression',
        returnType: async () => 'string',
        category: 'Select Options',
      },
      autoSelectFirstChild: {
        label: 'Auto Select First Child Expression',
        type: 'booleanExpression',
        category: 'Select Options',
      },
      onClickFunction: {
        label: 'On Click',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      updateAllowed: {
        label: 'Update Allowed',
        type: 'booleanExpression',
        category: 'Select Options',
      },
      margin: {
        label: 'Margin',
        type: 'margin',
        category: 'Spacing',
      },
      padding: {
        label: 'Padding',
        type: 'padding',
        category: 'Spacing',
      },
      maxWidth: {
        label: 'Max Width',
        category: 'Spacing',
        type: 'string',
      },
    },
    isPropertyVisible: (property, itemData) => {
      switch (property) {
        case 'labelAttribute':
        case 'idAttribute':
        case 'parentIdAttribute':
        case 'doNotPrefetch':
        case 'autoSelectFirstChild':
          return !!itemData.comboDatasourceAlias;
        default:
          return true;
      }
    },
    isDataSourceAliasUsed: (item, alias) =>
      item.props.comboDatasourceAlias === alias,
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV' ? { aliasProp: 'comboDatasourceAlias' } : undefined,
    category: 'Dashboard',
    icon: { iconName: 'list-alt' },
    description:
      'Breadcrumb Navigation can be used as multilevel nested menu, for navigational purpose.',
    helpRenders,
  };
};

export default BreadcrumbNavigationManager;
